/**
 * App Registry API (add-only; not used by call sites yet).
 * Single source for route→appKey, hub apps, recent-activity apps.
 * No UI strings, no i18n.
 */

import type { AppDescriptor } from "./types";

const APP_DESCRIPTORS: AppDescriptor[] = [
  {
    key: "matchmixer",
    basePath: "matchmixer",
    importApp: () => import("@tennis-matches/matchmixer"),
    hub: { section: "main", order: 0, enabledFlagKey: "schleifchen" },
    recentActivity: true,
    ai: { area: "matchmixer" },
  },
  {
    key: "clubmeisterschaft",
    basePath: "clubmeisterschaft",
    importApp: () =>
      import("@tennis-matches/clubmeisterschaft-core/ui").then((m) => ({
        default: m.ClubmeisterschaftRoot,
      })),
    hub: { section: "tools", order: 1, enabledFlagKey: "clubmeisterschaft" },
    recentActivity: true,
    ai: { area: "clubmeisterschaft" },
  },
  {
    key: "medenspiele",
    basePath: "medenspiele",
    importApp: () => import("@tennis-matches/medenspiele"),
    hub: { section: "tools", order: 2, enabledFlagKey: "medenspiele" },
    recentActivity: true,
  },
  {
    key: "minigames",
    basePath: "minigames",
    importApp: () => import("@tennis-matches/minigames"),
    hub: { section: "main", order: 3, enabledFlagKey: "minigames" },
  },
  {
    key: "share",
    basePath: "share",
    importApp: () => import("@tennis-matches/share"),
    hub: { section: "tools" },
  },
  {
    key: "trainerstunden",
    basePath: "trainerstunden",
    importApp: () => import("@tennis-matches/trainerstunden"),
    hub: { section: "tools", order: 0, enabledFlagKey: "trainerstunden" },
    recentActivity: true,
    ai: { area: "coachdesk" },
  },
  {
    key: "trainer",
    basePath: "trainer",
    importApp: () =>
      import("@tennis-matches/trainer").then((m) => ({ default: m.default })),
    hub: { section: "tools", order: 1, enabledFlagKey: "trainer" },
    ai: { area: "trainer" },
  },
];

const byKey = new Map<string, AppDescriptor>(
  APP_DESCRIPTORS.map((d) => [d.key, d]),
);

/** All registered apps. */
export function getAllApps(): AppDescriptor[] {
  return APP_DESCRIPTORS.slice();
}

/** Get descriptor by key. */
export function getAppByKey(key: string): AppDescriptor | null {
  if (!key || typeof key !== "string") return null;
  return byKey.get(key.trim()) ?? null;
}

/** Route/pathname → app key. Longest basePath prefix wins. */
export function resolveAppKeyForRoute(pathname: string): string | null {
  const r = String(pathname ?? "")
    .trim()
    .replace(/^\//, "");
  if (!r) return null;
  const byBaseLength = [...APP_DESCRIPTORS].sort(
    (a, b) => b.basePath.length - a.basePath.length,
  );
  for (const app of byBaseLength) {
    const base = app.basePath;
    if (r === base || r.startsWith(base + "-") || r.startsWith(base + "/"))
      return app.key;
    if (
      app.key === "matchmixer" &&
      (r.startsWith("schleifchenturnier") ||
        r.startsWith("schleifchen") ||
        r === "setup" ||
        r === "schedule" ||
        r === "entry" ||
        r === "finish" ||
        r === "results" ||
        r === "round-config")
    )
      return app.key;
  }
  if (r === "share" || r.startsWith("share-")) return "share";
  return null;
}

/** Hub apps, optionally filtered by feature flags. */
export function getHubApps(
  flags?: Map<string, boolean> | Record<string, boolean>,
): AppDescriptor[] {
  let list = APP_DESCRIPTORS.filter((a) => a.hub);
  if (flags != null) {
    const map = flags instanceof Map ? flags : new Map(Object.entries(flags));
    if (map.size > 0) {
      list = list.filter((a) => {
        const fk = a.hub?.enabledFlagKey;
        return !fk || map.get(fk) === true;
      });
    }
  }
  return list.sort((a, b) => (a.hub?.order ?? 99) - (b.hub?.order ?? 99));
}

/** Apps that appear in recent-activity list. */
export function getRecentActivityApps(): AppDescriptor[] {
  return APP_DESCRIPTORS.filter((a) => a.recentActivity === true);
}
